import { Injectable } from '@angular/core';

export const SWP_CONSTRAINTS = {
  initialInvestment: { min: 50_000, max: 100_000_000, step: 10_000 },
  monthlyWithdrawal: { min: 500, max: 1_000_000, step: 500 },
  rate:              { min: 1,   max: 30,       step: 0.1 },
  tenureYears:       { min: 1,   max: 40,       step: 1 }
} as const;

export interface SwpMonthRow {
  month: number;
  withdrawal: number;
  interest: number;
  balance: number;
}

export interface SwpYearRow {
  year: number;
  withdrawnThisYear: number;
  balance: number;
}

export interface SwpResult {
  months: SwpMonthRow[];
  years: SwpYearRow[];
  totalWithdrawn: number;
  finalBalance: number;
  depletedAtMonth: number | null;
}

@Injectable({ providedIn: 'root' })
export class SwpCalculatorService {

  calculate(
    initialInvestment: number,
    monthlyWithdrawal: number,
    annualRate: number,
    tenureYears: number
  ): SwpResult {
    const monthlyRate = annualRate / 100 / 12;
    const totalMonths = tenureYears * 12;

    let balance = initialInvestment;
    let totalWithdrawn = 0;
    let depletedAtMonth: number | null = null;

    const months: SwpMonthRow[] = [];

    for (let m = 1; m <= totalMonths; m++) {
      const interest = balance * monthlyRate;
      balance += interest;

      const withdrawal = Math.min(monthlyWithdrawal, balance);
      balance -= withdrawal;
      totalWithdrawn += withdrawal;

      months.push({ month: m, withdrawal, interest, balance });

      if (balance <= 0.5) {
        depletedAtMonth = m;
        break;
      }
    }

    const years: SwpYearRow[] = [];
    for (let y = 1; y <= Math.ceil(months.length / 12); y++) {
      const yearMonths = months.filter(m => Math.ceil(m.month / 12) === y);
      const last = yearMonths[yearMonths.length - 1];
      years.push({
        year: y,
        withdrawnThisYear: yearMonths.reduce((s, m) => s + m.withdrawal, 0),
        balance: last.balance
      });
    }

    return { months, years, totalWithdrawn, finalBalance: balance, depletedAtMonth };
  }
}
